import { Ulid } from "@kase/my-marathon-communication";
import { dateFromApi, dateToApi } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { hasPatchChanges } from "../utils";
import {
    Budget,
    BudgetFeeLine,
    BudgetFeeLineUpdateRequest,
    BudgetLine,
    BudgetOtherLine,
    BudgetOtherLineUpdateRequest,
    BudgetPurchaseLine,
    BudgetPurchaseLineUpdateRequest,
} from "./interfaces";

// A - fee
// I - purchase
// O - other
// P - phase
//
export const makeLine = ({ connection, company }: ApiSliceParams) => {
    const create = (budget: Budget, line: BudgetLine) => {
        const con = connection.value;

        if (line.t === "A") {
            return con.budget.budgetline.createA({
                budget: budget._id,
                company: company.value,
                lineA: line.data,
            });
        }

        if (line.t === "I") {
            return con.budget.budgetline.createI({
                budget: budget._id,
                company: company.value,
                lineI: line.data,
            });
        }

        return con.budget.budgetline.createO({
            budget: budget._id,
            company: company.value,
            lineO: line.data,
        });
    };

    const updateFee = async (
        record: BudgetFeeLine,
        params: BudgetFeeLineUpdateRequest,
    ) => {
        const con = connection.value;
        const changed = hasPatchChanges(record.data, params, true);

        if (!changed) {
            return false;
        }

        await con.budget.budgetline.updateA({
            id: record._id,
            company: company.value,
            lineA: params,
        });

        return true;
    };

    const updatePurchase = async (
        record: BudgetPurchaseLine,
        params: BudgetPurchaseLineUpdateRequest,
    ) => {
        const con = connection.value;
        const changed = hasPatchChanges(record.data, params, true);

        if (!changed) {
            return false;
        }

        await con.budget.budgetline.updateI({
            id: record._id,
            company: company.value,
            lineI: params,
        });

        return true;
    };

    const updateOther = async (
        record: BudgetOtherLine,
        params: BudgetOtherLineUpdateRequest,
    ) => {
        const con = connection.value;
        const changed = hasPatchChanges(record.data, params, true);

        if (!changed) {
            return false;
        }

        await con.budget.budgetline.updateO({
            id: record._id,
            company: company.value,
            lineO: params,
        });

        return true;
    };

    const move = async (
        records: BudgetLine[],
        budget: Budget,
        target: Readonly<Ulid>,
    ) => {
        const con = connection.value;
        const phase = budget.phases.peek().find((p) => p._id.equals(target));
        const offset = phase?.lines.peek().at(-1)?.data.position ?? 0;
        const promises: Promise<unknown>[] = [];
        let position = offset + 1;

        for (const line of records) {
            if (line.t === "A") {
                promises.push(
                    con.budget.budgetline.updateA({
                        company: company.value,
                        id: line._id,
                        lineA: {
                            phase: target,
                            position: position++,
                        },
                    }),
                );
            } else if (line.t === "I") {
                promises.push(
                    con.budget.budgetline.updateI({
                        company: company.value,
                        id: line._id,
                        lineI: {
                            phase: target,
                            position: position++,
                        },
                    }),
                );
            } else if (line.t === "O") {
                promises.push(
                    con.budget.budgetline.updateO({
                        company: company.value,
                        id: line._id,
                        lineO: {
                            phase: target,
                            position: position++,
                        },
                    }),
                );
            }
        }

        await Promise.allSettled(promises);
    };

    const copy = async (
        records: BudgetLine[],
        sourceBudget: Budget,
        targetPhase: Readonly<Ulid> | undefined,
        targetBudget?: Readonly<Ulid>,
        dateOffset?: { oldStart: Date | null; newStart: Date | null },
    ) => {
        const con = connection.value;
        const { oldStart, newStart } = dateOffset ?? {};
        const phase = targetPhase
            ? sourceBudget.phases.peek().find((p) => p._id.equals(targetPhase))
            : undefined;
        const offset = phase?.lines.peek().at(-1)?.data.position ?? 0;
        const promises: Promise<unknown>[] = [];
        let position = offset + 1;

        const getDates = ({
            date_start,
            date_end,
        }: {
            date_start?: number;
            date_end?: number;
        }) => {
            if (!date_start || !date_end || !oldStart || !newStart) {
                return { date_start, date_end };
            }

            const oldDelta =
                dateFromApi(date_start).getTime() - oldStart.getTime();
            const duration =
                dateFromApi(date_end).getTime() -
                dateFromApi(date_start).getTime();
            const newDateStart = new Date(newStart.getTime() + oldDelta);
            const newDateEnd = new Date(
                newStart.getTime() + oldDelta + duration,
            );

            return {
                date_start: dateToApi(newDateStart),
                date_end: dateToApi(newDateEnd),
            };
        };

        for (const line of records) {
            if (line.t === "A") {
                promises.push(
                    con.budget.budgetline.createA({
                        company: company.value,
                        budget: targetBudget ?? sourceBudget._id,
                        lineA: {
                            ...line.data,
                            ...getDates(line.data),
                            position: position++,
                            phase: targetPhase,
                        },
                    }),
                );
            } else if (line.t === "I") {
                promises.push(
                    con.budget.budgetline.createI({
                        company: company.value,
                        budget: targetBudget ?? sourceBudget._id,
                        lineI: {
                            ...line.data,
                            ...getDates(line.data),
                            purchase_category:
                                line.data.purchase_category &&
                                Ulid.ZERO.equals(line.data.purchase_category)
                                    ? undefined
                                    : line.data.purchase_category,
                            position: position++,
                            phase: targetPhase,
                        },
                    }),
                );
            } else if (line.t === "O") {
                promises.push(
                    con.budget.budgetline.createO({
                        company: company.value,
                        budget: targetBudget ?? sourceBudget._id,
                        lineO: {
                            ...line.data,
                            position: position++,
                            phase: targetPhase,
                        },
                    }),
                );
            }
        }

        await Promise.allSettled(promises);
    };

    const remove = (id: Readonly<Ulid>) =>
        connection.value.budget.budgetline.delete({
            company: company.value,
            id,
        });

    return {
        create,
        updateFee,
        updatePurchase,
        updateOther,
        move,
        copy,
        remove,
    };
};
